import { useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Group,
  LoadingOverlay,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { IconSortAscendingLetters } from "@tabler/icons-react";

import SponsorCard from "./SponsorCard";
import { useDeleteSponsor, useReorderSponsors } from "../../api/sponsors";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm";

const UNASSIGNED_KEY = "unassigned";

function groupSponsorsByLevel(sponsors, levels) {
  const sortedLevels = [...(levels || [])].sort((a, b) => a.order - b.order);

  const groups = sortedLevels.map((level) => ({
    key: String(level.id),
    levelId: level.id,
    name: level.name,
    sponsors: [],
  }));

  groups.push({
    key: UNASSIGNED_KEY,
    levelId: null,
    name: "Unassigned",
    sponsors: [],
  });

  const groupByLevelId = new Map(
    groups.map((g) => [g.levelId === null ? UNASSIGNED_KEY : g.levelId, g])
  );

  for (const sponsor of sponsors || []) {
    const key = sponsor.levelId ?? UNASSIGNED_KEY;
    const group = groupByLevelId.get(key);
    if (group) {
      group.sponsors.push(sponsor);
    }
  }

  groups.forEach((g) => {
    g.sponsors.sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.id - b.id;
    });
  });

  return groups;
}

function SponsorsBoard({ sponsors, levels, sponsorReps, eventId, isLoading }) {
  const initialGroups = useMemo(
    () => groupSponsorsByLevel(sponsors, levels),
    [sponsors, levels]
  );

  const [groups, setGroups] = useState(initialGroups);
  const [prevInitialGroups, setPrevInitialGroups] = useState(initialGroups);

  if (prevInitialGroups !== initialGroups) {
    setPrevInitialGroups(initialGroups);
    setGroups(initialGroups);
  }

  const repsBySponsor = useMemo(() => {
    const map = new Map();
    (sponsorReps || []).forEach((rep) => {
      if (!map.has(rep.sponsorId)) map.set(rep.sponsorId, []);
      map.get(rep.sponsorId).push(rep);
    });
    return map;
  }, [sponsorReps]);

  const { mutate: reorderMutate } = useReorderSponsors();
  const { mutate: deleteMutate } = useDeleteSponsor();
  const confirmDelete = useDeleteConfirm("sponsor", deleteMutate);

  const [activeDragSource, setActiveDragSource] = useState(null);

  const onDragStart = (start) => {
    setActiveDragSource(start.source.droppableId);
  };

  const isGroupSortedAlphabetically = (group) => {
    if (!group || group.sponsors.length < 2) return true;
    for (let i = 1; i < group.sponsors.length; i += 1) {
      const prev = group.sponsors[i - 1].name || "";
      const curr = group.sponsors[i].name || "";
      if (prev.localeCompare(curr, undefined, { sensitivity: "base" }) > 0) {
        return false;
      }
    }
    return true;
  };

  const sortGroupAlphabetically = (group) => {
    if (!group || group.key === UNASSIGNED_KEY) return;
    if (group.sponsors.length < 2) return;

    const sorted = [...group.sponsors].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      })
    );

    const isSameOrder = sorted.every(
      (sponsor, i) => sponsor.id === group.sponsors[i].id
    );
    if (isSameOrder) return;

    setGroups((prev) =>
      prev.map((g) => (g.key === group.key ? { ...g, sponsors: sorted } : g))
    );

    reorderMutate(
      {
        eventId,
        levelId: group.levelId,
        sponsorIds: sorted.map((s) => Number(s.id)),
      },
      {
        onError: () => {
          setGroups(initialGroups);
        },
      }
    );
  };

  const onDragEnd = (result) => {
    setActiveDragSource(null);

    const { source, destination } = result;
    if (!destination) return;
    if (
      source.droppableId !== destination.droppableId ||
      source.index === destination.index
    ) {
      return;
    }

    const groupKey = source.droppableId;
    const group = groups.find((g) => g.key === groupKey);
    if (!group || group.key === UNASSIGNED_KEY) return;

    const reordered = Array.from(group.sponsors);
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);

    setGroups((prev) =>
      prev.map((g) => (g.key === groupKey ? { ...g, sponsors: reordered } : g))
    );

    reorderMutate(
      {
        eventId,
        levelId: group.levelId,
        sponsorIds: reordered.map((s) => Number(s.id)),
      },
      {
        onError: () => {
          setGroups(initialGroups);
        },
      }
    );
  };

  if (!isLoading && (!sponsors || sponsors.length === 0)) {
    return (
      <Paper withBorder p="xl" radius="md" style={{ textAlign: "center" }}>
        <Stack gap={4} align="center">
          <Title order={4}>No sponsors yet</Title>
          <Text size="sm" c="dimmed">
            Use the “Create” button above to add your first sponsor.
          </Text>
        </Stack>
      </Paper>
    );
  }

  return (
    <Box style={{ position: "relative" }}>
      <LoadingOverlay visible={isLoading} />
      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
          {groups
            .filter((g) => g.key !== UNASSIGNED_KEY || g.sponsors.length > 0)
            .map((group, index) => (
              <Paper key={group.key} withBorder radius="md" p="lg" bg="gray.0">
                <Group justify="space-between" mb="md">
                  <Box
                    pl={4}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      lineHeight: 1,
                    }}
                  >
                    {group.key !== UNASSIGNED_KEY && (
                      <Text
                        component="span"
                        c="dimmed"
                        fw={500}
                        style={{
                          fontSize: "var(--mantine-h3-font-size)",
                          lineHeight: 1,
                        }}
                      >
                        #{index + 1}
                      </Text>
                    )}
                    <Title
                      order={3}
                      tt="capitalize"
                      style={{ lineHeight: 1, margin: 0 }}
                    >
                      {group.name}
                    </Title>
                  </Box>
                  <Group gap="xs">
                    {group.key !== UNASSIGNED_KEY &&
                      (() => {
                        const isSorted = isGroupSortedAlphabetically(group);
                        return (
                          <Tooltip
                            label={
                              isSorted
                                ? "Already sorted alphabetically"
                                : "Sort alphabetically"
                            }
                            withArrow
                          >
                            <ActionIcon
                              variant="subtle"
                              color={isSorted ? "gray" : "blue"}
                              size="lg"
                              onClick={() => sortGroupAlphabetically(group)}
                              disabled={isSorted}
                              aria-label="Sort alphabetically"
                            >
                              <IconSortAscendingLetters size={18} />
                            </ActionIcon>
                          </Tooltip>
                        );
                      })()}
                    <Badge variant="light" color="gray" size="lg">
                      {group.sponsors.length}
                    </Badge>
                  </Group>
                </Group>

                <Droppable
                  droppableId={group.key}
                  isDropDisabled={
                    group.key === UNASSIGNED_KEY ||
                    (activeDragSource !== null &&
                      activeDragSource !== group.key)
                  }
                >
                  {(provided, snapshot) => (
                    <Stack
                      gap="xs"
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        minHeight: 60,
                        padding: 4,
                        borderRadius: 6,
                        backgroundColor: snapshot.isDraggingOver
                          ? "var(--mantine-color-blue-0)"
                          : "transparent",
                        transition: "background-color 120ms ease",
                      }}
                    >
                      {group.sponsors.length === 0 && (
                        <Text size="xs" c="dimmed" ta="center" py="md">
                          {group.key === UNASSIGNED_KEY
                            ? "No unassigned sponsors"
                            : "No sponsors in this level yet"}
                        </Text>
                      )}
                      {group.sponsors.map((sponsor, index) => (
                        <Draggable
                          key={sponsor.id}
                          draggableId={String(sponsor.id)}
                          index={index}
                          isDragDisabled={group.key === UNASSIGNED_KEY}
                        >
                          {(dragProvided, dragSnapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              style={dragProvided.draggableProps.style}
                            >
                              <SponsorCard
                                sponsor={sponsor}
                                reps={repsBySponsor.get(sponsor.id)}
                                dragHandleProps={dragProvided.dragHandleProps}
                                isDragging={dragSnapshot.isDragging}
                                onDelete={() =>
                                  confirmDelete({ id: sponsor.id })
                                }
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Stack>
                  )}
                </Droppable>
              </Paper>
            ))}
        </SimpleGrid>
      </DragDropContext>
    </Box>
  );
}

export default SponsorsBoard;
